import { MiqDeltaInput } from "../../types/miq/miq-diff";
import { Config } from "../../types/config/config";
import { Listing } from "../../types/listing/listing";
import { findSpacesFields } from "../config/spaces-fields";
import { findSpecificationFields } from "../config/specifications-fields";

export type MiqFieldUpdateStatus = "new" | "discarded";

export interface MiqFieldUpdate {
  field: string;
  label: string;
  currentValue: string;
  miqValue: string;
  status: MiqFieldUpdateStatus;
  delta: MiqDeltaInput;
}

export interface MiqListingUpdates {
  listingId: string;
  updates: MiqFieldUpdate[];
}

const stripJsonPathPrefix = (jsonPath: string): string =>
  (jsonPath || "").replace(/^\$\.?/, "");

const getPathField = (jsonPath: string): string => {
  const path = stripJsonPathPrefix(jsonPath);
  return path
    .split(".")
    .map((segment) => segment.replace(/\[.*$/, "").trim())
    .filter(Boolean)
    .map((segment) => segment.replace(/([A-Z])/g, " $1").trim())
    .join(".");
};

const formatStructuredValue = (value: any, path = ""): string[] => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [path ? `${path}: []` : "[]"];
    }
    return value.reduce(
      (lines: string[], item: any, index: number) =>
        lines.concat(formatStructuredValue(item, `${path}[${index + 1}]`)),
      [],
    );
  }
  if (value && typeof value === "object") {
    if (Object.keys(value).length === 0) {
      return [path ? `${path}: {}` : "{}"];
    }
    return Object.keys(value).reduce(
      (lines: string[], key: string) =>
        lines.concat(
          formatStructuredValue(value[key], path ? `${path}.${key}` : key),
        ),
      [],
    );
  }
  const displayValue =
    value === null || value === undefined ? "-" : String(value);
  return [path ? `${path}: ${displayValue}` : displayValue];
};

const formatMiqValue = (value: string | undefined): string => {
  if (value === undefined || value === null || value === "undefined") {
    return "null";
  }
  try {
    const parsedValue = JSON.parse(value);
    if (parsedValue && typeof parsedValue === "object") {
      return formatStructuredValue(parsedValue).join("\n");
    }
  } catch (error) {
    // Non-JSON values are already display-ready.
  }
  return value;
};

const toClientField = (field: string): string =>
  field.charAt(0).toLowerCase() + field.slice(1);

const getDeltaField = (jsonPath: string): string => {
  const path = stripJsonPathPrefix(jsonPath);

  return path
    .split(/[.[\]]+/)
    .filter(Boolean)
    .map(toClientField)
    .join(".");
};

const normalizeMatchPath = (field: string): string =>
  field.replace(/\.\d+(?=\.|$)/g, "").toLowerCase();

const getConfiguredFields = (config: Config, listing: Listing): string[] => {
  const fields: string[] = [];
  const add = (field: string) => {
    if (fields.indexOf(field) === -1) {
      fields.push(field);
    }
  };

  // config keys whose name doesn't match the corresponding Listing field name
  const configFieldAliases: { [configKey: string]: string } = {
    sizesandmeasurements: "propertySizes",
    demographics: "demoGraphics",
    highlight: "highlights",
  };

  // handled precisely below via addNestedFields (per-field show flags), so skip the generic root match
  const preciselyHandledConfigKeys = ["specifications", "spaces"];

  // Config object order matches the order used by the property section.
  Object.keys(config).forEach((configKey) => {
    if (preciselyHandledConfigKeys.indexOf(configKey) > -1) {
      return;
    }
    const configField = (config as any)[configKey];
    const listingField =
      configFieldAliases[configKey] &&
      Object.prototype.hasOwnProperty.call(
        listing,
        configFieldAliases[configKey],
      )
        ? configFieldAliases[configKey]
        : Object.prototype.hasOwnProperty.call(listing, configKey)
        ? configKey
        : configKey.endsWith("s") &&
          Object.prototype.hasOwnProperty.call(listing, configKey.slice(0, -1))
        ? configKey.slice(0, -1)
        : undefined;
    if (
      listingField &&
      configField &&
      typeof configField === "object" &&
      configField.show !== false
    ) {
      add(listingField);
    }
  });

  const addNestedFields = (fieldSet: any, prefix: string) => {
    if (!fieldSet) {
      return;
    }
    Object.keys(fieldSet)
      .filter(
        (key) =>
          fieldSet[key] &&
          fieldSet[key].show &&
          fieldSet[key].properties &&
          fieldSet[key].properties.name,
      )
      .sort(
        (first, second) =>
          (fieldSet[first].order || 0) - (fieldSet[second].order || 0),
      )
      .forEach((key) => add(prefix + fieldSet[key].properties.name));
  };

  addNestedFields(findSpecificationFields(config, listing), "specifications.");
  addNestedFields(
    findSpacesFields(config, listing.propertyType, listing.listingType),
    "spaces.",
  );
  return fields;
};

export const mapMiqDiffs = (
  listing: Listing,
  config: Config,
  deltas: MiqDeltaInput[],
  discardedFields: { [field: string]: string | undefined } = {},
): MiqListingUpdates => {
  const configuredFields = getConfiguredFields(config, listing);
  const shouldFilterByConfiguredFields = configuredFields.length > 0;

  // matches a delta field against a configured field by exact match or as a nested/array member of it
  const matchConfiguredIndex = (field: string): number | undefined => {
    let bestIndex: number | undefined;
    let bestLength = -1;
    const normalizedField = normalizeMatchPath(field);
    configuredFields.forEach((configuredField, index) => {
      const normalizedConfiguredField = normalizeMatchPath(configuredField);
      const isMatch =
        normalizedField === normalizedConfiguredField ||
        normalizedField.startsWith(`${normalizedConfiguredField}.`);
      if (isMatch && normalizedConfiguredField.length > bestLength) {
        bestLength = normalizedConfiguredField.length;
        bestIndex = index;
      }
    });
    return bestIndex;
  };

  const updates = (deltas || [])
    .map((delta, index) => ({
      delta,
      index,
      field: getDeltaField(delta.jsonPath),
    }))
    .map((item) => ({
      ...item,
      configuredIndex: matchConfiguredIndex(item.field),
    }))
    .filter(
      (item) =>
        !shouldFilterByConfiguredFields || item.configuredIndex !== undefined,
    )
    .sort((first, second) =>
      shouldFilterByConfiguredFields
        ? first.configuredIndex! - second.configuredIndex! ||
          first.index - second.index
        : first.index - second.index,
    )
    .map((item, index) => ({
      field: item.delta.jsonPath || `delta-${index}`,
      label: getPathField(item.delta.jsonPath),
      currentValue: formatMiqValue(item.delta.deValue),
      miqValue: formatMiqValue(item.delta.newValue),
      status:
        Object.prototype.hasOwnProperty.call(
          discardedFields,
          item.delta.jsonPath,
        ) && discardedFields[item.delta.jsonPath] === item.delta.newValue
          ? ("discarded" as MiqFieldUpdateStatus)
          : ("new" as MiqFieldUpdateStatus),
      delta: item.delta,
    }))
    .sort((first, second) =>
      first.status === second.status
        ? 0
        : first.status === "discarded"
        ? 1
        : -1,
    );
  return { listingId: String(listing.id), updates };
};
